import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiFetch from "../../utlis/apiFetch";
import { forceLogout } from "../../utlis/forceLogout";

const handleAuthError = (error, rejectWithValue) => {
  if (error.message === "Your session has expired") forceLogout();
  return rejectWithValue(error.message);
};

export const fetchBankDetailsByCountry = createAsyncThunk(
  "bankDetails/fetchByCountry",
  async (country, { getState, rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const { bankDetailsByCountry } = getState().bankDetailsByCountry;
      if (bankDetailsByCountry[country]) {
        return {
          data: bankDetailsByCountry[country],
          country,
          fromCache: true,
        };
      }

      const apiUrl = `${
        process.env.REACT_APP_API_BASE_URL
      }/api/bankdetails/${encodeURIComponent(country)}`;

      const response = await apiFetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response._data ?? await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch bank details");
      }

      return {
        data: data.body,
        country,
        fromCache: false,
      };
    } catch (err) {
      return handleAuthError(err, rejectWithValue);
    }
  }
);

const bankDetailsByCountrySlice = createSlice({
  name: "bankDetailsByCountry",
  initialState: {
    bankDetailsByCountry: {},
    isLoading: false,
    error: null,
    currentBankDetails: null,
  },
  reducers: {
    clearBankDetailsData: (state) => {
      state.bankDetailsByCountry = {};
      state.currentBankDetails = null;
    },
    setCurrentBankDetails: (state, action) => {
      const country = action.payload;

      if (state.bankDetailsByCountry[country]) {
        state.currentBankDetails = state.bankDetailsByCountry[country];
      } else {
        state.currentBankDetails = null;
      }
    },
    clearCurrentBankDetails: (state) => {
      state.currentBankDetails = null;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBankDetailsByCountry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBankDetailsByCountry.fulfilled, (state, action) => {
        state.isLoading = false;

        if (!action.payload.fromCache) {
          state.bankDetailsByCountry[action.payload.country] =
            action.payload.data;
        }
        state.currentBankDetails = action.payload.data;
      })
      .addCase(fetchBankDetailsByCountry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const selectBankDetailsByCountry = (state, country) => {
  return state.bankDetailsByCountry.bankDetailsByCountry[country] || null;
};

export const selectCurrentBankDetails = (state) =>
  state.bankDetailsByCountry.currentBankDetails;
export const selectIsLoading = (state) => state.bankDetailsByCountry.isLoading;
export const selectError = (state) => state.bankDetailsByCountry.error;

export const {
  clearBankDetailsData,
  setCurrentBankDetails,
  clearCurrentBankDetails,
} = bankDetailsByCountrySlice.actions;
export default bankDetailsByCountrySlice.reducer;
